import { concat, get } from '@ember/helper';
import { get as _get } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismCard, PrismTabsContainer } from '@mylighthouse/prism-ember';
import { camelize } from 'frontend-utils/utils/convert-case';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import HotelAndMarketRevenueInsightKpiKpiListItem from 'lumen/components/hotel-and-market/revenue-insight/kpi/kpi-list-item';
import HotelAndMarketRevenueInsightLoadingStateTitle from 'lumen/components/hotel-and-market/revenue-insight/loading-state-title';
import highlightElement from 'lumen/modifiers/highlight-element';
import { KPI_CATEGORIES, KPIS } from 'lumen/utils/constants/ri-constant-values';

interface HotelAndMarketRevenueInsightKpiKpiSectionSignature {
  Args: {
    changeSelectedKpi: unknown;
    changeSelectedKpiCategory: unknown;
    isLoading?: boolean;
    kpi: unknown;
    kpiBlocks: unknown;
    kpiCategory: unknown;
    visibleKpis: unknown;
  };
}

export default class HotelAndMarketRevenueInsightKpiKpiSection extends Component<HotelAndMarketRevenueInsightKpiKpiSectionSignature> {
  @service declare access: Services['access'];
  @service declare intl: Services['intl'];
  @service declare riSelections: Services['ri-selections'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get availableKpiCategoriesHierarchy() {
    const availableKpiCategoriesHierarchy = [];

    // Sort function for KpiCategories to mimic the order in the PMS settings.
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function kpiCategoriesSortFunction(a, b) {
      const kpiCategoriesSortOrder = [
        KPI_CATEGORIES.TRANSIENT,
        KPI_CATEGORIES.GROUP,
        KPI_CATEGORIES.CONTRACTED_CREW,
        KPI_CATEGORIES.OTHER,
        KPI_CATEGORIES.UNMAPPED,
      ];
      return (
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument
        kpiCategoriesSortOrder.indexOf(a) - kpiCategoriesSortOrder.indexOf(b)
      );
    }

    if (this.riSelections.availableKpiCategories.length) {
      // We get a flattened list from API (the meta field) containing the KpiCategories to show, but it needs to be a hierarchy in order for us to show nested rows.
      // Every KpiCategory should be a child of 'total'.
      // If total is in the meta KpiCategories, use it as parent and add the rest of the meta KpiCategories as children.
      // Otherwise just map all the meta KpiCategories to the correct object format for us to use.
      if (
        this.riSelections.availableKpiCategories.includes(KPI_CATEGORIES.TOTAL)
      ) {
        const totalKpiCategory = {
          name: KPI_CATEGORIES.TOTAL,
          children: this.riSelections.availableKpiCategories
            .filter(
              (availableKpiCategory) =>
                availableKpiCategory !== KPI_CATEGORIES.TOTAL,
            )
            .sort(kpiCategoriesSortFunction)
            .map((availableKpiCategory) => ({
              name: availableKpiCategory,
            })),
        };

        availableKpiCategoriesHierarchy.push(totalKpiCategory);
      } else {
        this.riSelections.availableKpiCategories.forEach(
          (availableKpiCategory) =>
            availableKpiCategoriesHierarchy.push({
              name: availableKpiCategory,
            }),
        );
      }
    }

    return availableKpiCategoriesHierarchy;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get contentParams() {
    return {
      isLoading: this.args.isLoading,
      kpi: this.args.kpi,
      kpiCategory: this.args.kpiCategory,
      changeSelectedKpi: this.args.changeSelectedKpi,
      changeSelectedKpiCategory: this.args.changeSelectedKpiCategory,
    };
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedData() {
    const dataDict = {};

    if (this.availableKpiCategoriesHierarchy.length) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.args.visibleKpis.forEach((kpi) => {
        // @ts-expect-error: Incorrect type
        const mappedKpiCategories = [];
        this.mapKpiCategories(
          this.availableKpiCategoriesHierarchy,
          kpi,
          // @ts-expect-error: Incorrect type
          mappedKpiCategories,
        );
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        dataDict[kpi] = mappedKpiCategories;
      });
    }

    return dataDict;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get kpiConfig() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.args.kpiBlocks.map((kpiBlock) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const hasMultipleTabs = kpiBlock.kpis.length > 1;

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const tabs = kpiBlock.kpis.map((kpi) => {
        let infoText = null;

        // Show an info icon with tooltip for Revenue tab.
        if (kpi === KPIS.REVENUE) {
          if (this.riSelections?.isCancellationsOnlyView) {
            infoText = this.intl.t(
              'revenue_insight.cancellations_revenue_explanation',
            );
          } else {
            infoText = this.intl.t(
              'revenue_insight.net_room_revenue_explanation',
            );
          }
        }

        return {
          title: this.intl.t(`revenue_insight.${kpi}`),
          class: 'detail-view',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          id: kpi,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          active: kpiBlock.activeTab === kpi,
          infoText,
        };
      });

      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        id: kpiBlock.id,
        hasMultipleTabs,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        tabs,
        // Only used for the tabcontainer, ideally this component is refactored so we only need to keep state of one kpi block
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        tabIds: tabs.map((tab) => tab.id),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        activeTab: kpiBlock.activeTab,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        changeTab: kpiBlock.changeTab,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        items:
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          this.formattedData[kpiBlock.activeTab] ??
          (this.args.isLoading && [{}]), // Add dummy element when there is no formattedData and the app is loading
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get kpiMaxValues() {
    const kpimaxValuesDict = {};
    const { bobKey, changeKey, availableKpiCategories } = this.riSelections;

    // Loop over each KPI block and KPI value key (revenue, adr, nrOfRooms, ...)
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.args.visibleKpis.forEach((kpi) => {
      let maxValue = 0;
      let maxChange = 0;
      let maxWithPLY = 0;

      // Get all metrics in one block list of KPIs (can be nested, but availableKpiCategories flattens that out)
      availableKpiCategories.forEach((availableKpiCategory) => {
        const kpiKey = camelize(`${kpi}_${availableKpiCategory}`);

        let currentValue =
          _get(this, `args.data.calculatedValues.${bobKey}.${kpiKey}`) ?? 0;

        if (
          kpi === KPIS.NR_OF_ROOMS &&
          this.riSelections?.isOccupancySelected
        ) {
          const occupancyKpiKey = camelize(
            `${KPIS.OCCUPANCY}_${availableKpiCategory}`,
          );

          currentValue =
            _get(
              this,
              `args.data.calculatedValues.${bobKey}.${occupancyKpiKey}`,
            ) ?? 0;
        }

        // Check if budget is active...
        if (this.riSelections?.isBudgetActive) {
          // If budget is active, use budget & budgetChange
          const currentBudgetValue =
            _get(this, `args.data.calculatedValues.budget.${kpiKey}`) ?? 0;
          const currentBudgetChange =
            Math.abs(
              // @ts-expect-error: Incorrect type
              _get(this, `args.data.calculatedValues.budgetChange.${kpiKey}`),
            ) ?? 0;
          const currentPLY =
            _get(this, `args.data.calculatedValues.pickupLastYear.${kpiKey}`) ??
            0;

          // Set new max values if they are greater
          // @ts-expect-error: Incorrect type
          maxValue = Math.max(currentBudgetValue, currentValue, maxValue);
          maxChange = Math.max(currentBudgetChange, maxChange);
          // Don't show Pickup Last Year for ADR
          if (kpi !== KPIS.ADR) {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/restrict-plus-operands, @typescript-eslint/restrict-plus-operands
            maxWithPLY = Math.max(currentValue + currentPLY, maxWithPLY);
            maxValue = Math.max(
              // @ts-expect-error: Incorrect type
              currentBudgetValue,
              maxWithPLY,
              currentValue,
              maxValue,
            );
          }
        } else if (this.riSelections?.isForecastActive) {
          // If forecast is active, use forecast & forecastChange
          const currentForecastValue =
            _get(this, `args.data.calculatedValues.forecast.${kpiKey}`) ?? 0;
          const currentForecastChange =
            Math.abs(
              // @ts-expect-error: Incorrect type
              _get(this, `args.data.calculatedValues.forecastChange.${kpiKey}`),
            ) ?? 0;
          const currentPLY =
            _get(this, `args.data.calculatedValues.pickupLastYear.${kpiKey}`) ??
            0;

          // Set new max values if they are greater
          // @ts-expect-error: Incorrect type
          maxValue = Math.max(currentForecastValue, currentValue, maxValue);
          maxChange = Math.max(currentForecastChange, maxChange);
          // Don't show Pickup Last Year for ADR
          if (kpi !== KPIS.ADR) {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/restrict-plus-operands, @typescript-eslint/restrict-plus-operands
            maxWithPLY = Math.max(currentValue + currentPLY, maxWithPLY);
            maxValue = Math.max(
              // @ts-expect-error: Incorrect type
              currentForecastValue,
              maxWithPLY,
              currentValue,
              maxValue,
            );
          }
        } else {
          // If budget is not active, use BOB & change
          const currentChange =
            Math.abs(
              // @ts-expect-error: Incorrect type
              _get(this, `args.data.calculatedValues.${changeKey}.${kpiKey}`),
            ) ?? 0;

          // Set new max values if they are greater
          // @ts-expect-error: Incorrect type
          maxValue = Math.max(currentValue, maxValue);
          maxChange = Math.max(currentChange, maxChange);
        }
      });
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      kpimaxValuesDict[kpi] = {
        BOB: maxValue,
        change: maxChange,
      };
    });

    return kpimaxValuesDict;
  }

  mapKpiCategories(
    // @ts-expect-error: Incorrect type
    kpiCategories,
    // @ts-expect-error: Incorrect type
    kpi,
    // @ts-expect-error: Incorrect type
    mappedKpiCategories,
    parent = null,
  ): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    kpiCategories.forEach((kpiCategory) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const kpiCategoryName = kpiCategory.name;

      const mappedKpiCategory = {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        kpi,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        calculatedValues: this.args.data?.calculatedValues,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        kpiCategory: kpiCategoryName,
        parent,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        segmentation: {
          // @ts-expect-error: Incorrect type
          ...this.args.data,
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          calculatedValues: this.args.data?.calculatedValues.calculateValues(),
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          parentId: parent?.segmentation?.id,
        },
      };

      const isTransientGroupFilterEnabled =
        this.riSelections.isMultiProperty ||
        this.access.hasFeature('revenueinsight.transient_group_filter');

      // @ts-expect-error: Incorrect type
      mappedKpiCategory.isTransientAndGroupFilterDisabled =
        parent && !isTransientGroupFilterEnabled;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      mappedKpiCategories.push(mappedKpiCategory);

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (kpiCategory.children) {
        this.mapKpiCategories(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          kpiCategory.children,
          kpi,
          mappedKpiCategories,
          // @ts-expect-error: Incorrect type
          mappedKpiCategory,
        );
      }
    });
  }

  <template>
    <div class="kpi-section-container ic-product-tour-kpi-section">
      {{#each this.kpiConfig as |kpiBlock|}}
        <div
          class="block-card block-card--equal-width"
          data-test-id="kpi-block-{{kpiBlock.id}}"
          {{highlightElement
            id=(concat "revenue-insight-kpi-block-" kpiBlock.activeTab)
            isLoading=@isLoading
          }}
        >
          {{#if kpiBlock.hasMultipleTabs}}
            <PrismCard @contentLayout="noPadding">
              <:content>
                <div class="card-content">
                  <PrismTabsContainer
                    @activeTabId={{kpiBlock.activeTab}}
                    @onSelectTab={{kpiBlock.changeTab}}
                    @visibleTabIds={{kpiBlock.tabIds}}
                    as |TabsContainer|
                  >
                    <TabsContainer.header as |Header|>
                      {{#each kpiBlock.tabs as |tab|}}
                        <Header.tab @id={{tab.id}}>
                          {{tab.title}}
                          {{#if tab.infoText}}
                            <span class="pl-100">
                              <GeneralIconWithTooltip
                                @color="inherit"
                                @side="top"
                                @tooltipContent={{tab.infoText}}
                              />
                            </span>
                          {{/if}}
                        </Header.tab>
                      {{/each}}
                    </TabsContainer.header>
                    <TabsContainer.content as |Content|>
                      {{#each kpiBlock.tabs as |tab|}}
                        <Content.tab @id={{tab.id}}>
                          <div class="block-list">
                            {{#each kpiBlock.items as |item|}}
                              <HotelAndMarketRevenueInsightKpiKpiListItem
                                @item={{item}}
                                {{! @glint-expect-error: Incorrect type }}
                                @maxValues={{this.kpiMaxValues}}
                                {{! @glint-expect-error: Incorrect type }}
                                @params={{this.contentParams}}
                              />
                            {{/each}}
                          </div>
                        </Content.tab>
                      {{/each}}
                    </TabsContainer.content>
                  </PrismTabsContainer>
                </div>
              </:content>
            </PrismCard>
          {{else}}
            <PrismCard @contentLayout="noPadding">
              <:header as |Header|>
                <div class="title-box flex items-center justify-start">
                  {{#if @isLoading}}
                    <HotelAndMarketRevenueInsightLoadingStateTitle />
                  {{else}}
                    <Header.title>
                      {{get kpiBlock.tabs "0.title"}}
                    </Header.title>
                  {{/if}}
                </div>
              </:header>

              <:content>
                <div class="block-list">
                  {{#each kpiBlock.items as |item|}}
                    <HotelAndMarketRevenueInsightKpiKpiListItem
                      @item={{item}}
                      {{! @glint-expect-error: Incorrect type }}
                      @maxValues={{this.kpiMaxValues}}
                      {{! @glint-expect-error: Incorrect type }}
                      @params={{this.contentParams}}
                    />
                  {{/each}}
                </div>
              </:content>
            </PrismCard>
          {{/if}}
        </div>
      {{/each}}
    </div>
  </template>
}
